<script lang="ts">
  /**
   * 問題の作業台 `#/problem/<id>`（problem-format.md §1、§7、§8）。1 画面に上から:
   * 見出し（出典・制限時間・AtCoder へのリンク）→ 問題（要約・制約・入出力・入出力例）→ まずこう考える →
   * エディタ・実行と採点・ヒント・模範解答 → 解説（要点と「基礎に立ち返る」、暗記事項）→ 一般化 → 類題。
   * 端末に残すのは書きかけのコードだけ。採点結果・開いたステップやヒントの数は保存しない。
   * difficulty は内部値なので出さない（§2.2）。
   */
  import BlockNodes from '../components/lesson/BlockNodes.svelte';
  import MemoList from '../components/lesson/MemoList.svelte';
  import { setLinkTargetCheck } from '../components/lesson/lesson-context';
  import EssentialCard from '../components/problem/EssentialCard.svelte';
  import ExerciseItem from '../components/problem/ExerciseItem.svelte';
  import SampleCases from '../components/problem/SampleCases.svelte';
  import SourceLink from '../components/problem/SourceLink.svelte';
  import ThinkSteps from '../components/problem/ThinkSteps.svelte';
  import Workbench from '../components/problem/Workbench.svelte';
  import { returnToProblem } from '../components/problem/return-to.svelte';
  import type { DraftStore, ProblemDoc } from '../components/problem/types';
  import { isMemoCard, techniqueLabel, type Card, type MemoCard } from '../lib/cards';
  import { problemDraftKey, problemLinkTarget, sampleCases, sourceLabel } from '../lib/problems';

  interface Props {
    doc: ProblemDoc;
    drafts: DraftStore;
    /** 暗記事項のカードを探す */
    findCard: (id: string) => Card | undefined;
  }

  let { doc, drafts, findCard }: Props = $props();

  const uid = $props.id();
  setLinkTargetCheck(problemLinkTarget);

  const meta = $derived(doc.meta);
  const memos = $derived(
    meta.memos
      .map((id) => findCard(id))
      .filter((c): c is MemoCard => c !== undefined && isMemoCard(c) && c.draft !== true),
  );

  /** この画面からレッスンへ移るリンク（要点・本文）を押したら、戻り先として覚える（§6.3） */
  function onclick(e: MouseEvent) {
    const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (a && (a.getAttribute('href') ?? '').startsWith('#/lesson/')) {
      returnToProblem.set(meta.id);
    }
  }
</script>

<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
<div class="page page-drill problem" data-testid="problem" data-problem-id={meta.id} {onclick}>
  <header class="head">
    <span class="eyebrow">問題 · {sourceLabel(meta.source)}</span>
    <h1 tabindex="-1">{meta.title}</h1>
    <p class="meta-line">
      <span data-testid="problem-time-limit"
        >制限時間 <span class="num">{meta.time_limit_sec}</span> 秒</span
      >
      <SourceLink url={meta.source.url} />
    </p>
    <ul class="tags" aria-label="手法">
      {#each meta.techniques as t (t)}<li class="tag">{techniqueLabel(t)}</li>{/each}
    </ul>
  </header>

  <section class="sec" aria-labelledby="{uid}-statement" data-testid="problem-statement">
    <h2 id="{uid}-statement">問題</h2>
    <BlockNodes blocks={doc.statement.summary} />
    <h3 class="h3">制約</h3>
    <BlockNodes blocks={doc.statement.constraints} />
    <h3 class="h3">入力</h3>
    <BlockNodes blocks={doc.statement.input} />
    <h3 class="h3">出力</h3>
    <BlockNodes blocks={doc.statement.output} />
    <h3 class="h3">入出力例</h3>
    <SampleCases cases={sampleCases(meta.cases)} />
  </section>

  <section class="sec" aria-labelledby="{uid}-think" data-testid="problem-think">
    <h2 id="{uid}-think">まずこう考える</h2>
    <p class="text-soft lead">自分で考えてから、1 つずつ開きます。</p>
    <ThinkSteps steps={doc.think} />
  </section>

  <section class="sec panel bench" aria-labelledby="{uid}-write" data-testid="problem-write">
    <h2 id="{uid}-write">実装</h2>
    <Workbench
      kind="problem"
      target={{ problemId: meta.id }}
      draftKey={problemDraftKey(meta.id)}
      starter={meta.starter}
      cases={meta.cases}
      solution={meta.solution}
      hints={doc.hints}
      timeLimitSec={meta.time_limit_sec}
      {drafts}
    />
  </section>

  <section class="sec" aria-labelledby="{uid}-explain" data-testid="problem-explanation">
    <h2 id="{uid}-explain">解説</h2>
    {#each doc.explanation as b, i (i)}
      {#if b.type === 'essential'}
        <EssentialCard block={b} />
      {:else}
        <BlockNodes blocks={[b]} />
      {/if}
    {/each}
    {#if memos.length > 0}
      <h3 class="h3">暗記事項</h3>
      <MemoList {memos} />
    {/if}
  </section>

  <section class="sec" aria-labelledby="{uid}-general" data-testid="problem-generalization">
    <h2 id="{uid}-general">一般化</h2>
    <BlockNodes blocks={doc.generalization} />
  </section>

  {#if doc.exercises.length > 0}
    <section class="sec" aria-labelledby="{uid}-ex" data-testid="problem-exercises">
      <h2 id="{uid}-ex">類題</h2>
      <BlockNodes blocks={doc.exercisesIntro} />
      <ol class="exercises">
        {#each doc.exercises as ex (ex.id)}
          <ExerciseItem problem={meta} exercise={ex} {drafts} />
        {/each}
      </ol>
    </section>
  {/if}
</div>

<style>
  .problem {
    display: flex;
    flex-direction: column;
    gap: var(--space-6);
    line-height: var(--lh-body);
  }
  .head {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .head h1 {
    font-size: var(--fs-2xl);
    line-height: var(--lh-tight);
  }
  .meta-line {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2) var(--space-4);
    margin: 0;
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .tag {
    font-size: var(--fs-xs);
    padding: 0 var(--space-2);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-xs);
    color: var(--text);
  }
  .sec {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    min-width: 0;
  }
  .sec > h2 {
    font-size: var(--fs-xl);
    padding-bottom: var(--space-2);
    border-bottom: 1px solid var(--border);
    margin-bottom: var(--space-2);
  }
  .bench {
    padding: var(--space-5);
  }
  .h3 {
    font-size: var(--fs-md);
    margin: var(--space-3) 0 var(--space-1);
  }
  .lead {
    margin: 0;
    font-size: var(--fs-sm);
  }
  .exercises {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  @media (max-width: 639px) {
    .bench {
      padding: var(--space-4) var(--space-3);
    }
  }
</style>
